/**
 * The languages a viewer chooses for their channel list. Providers carry the
 * whole world (hundreds of thousands of movies in dozens of languages);
 * loading only the viewer's languages makes the list several times smaller,
 * so phones and TVs load it in seconds. Shared by the app (the picker), the
 * account server (checks the choice) and the relay (filters the lists). No
 * imports: the account server and the relay use this file as it is.
 */

/** Names as category-tags.ts reads them from category names. */
export const CATALOG_LANGUAGES = [
  'Arabic', 'French', 'English', 'Spanish', 'German', 'Italian', 'Portuguese', 'Turkish', 'Dutch', 'Polish', 'Romanian', 'Greek',
  'Albanian', 'Serbo-Croatian', 'Russian', 'Hindi', 'Urdu', 'Persian', 'Swedish', 'Danish', 'Norwegian', 'Finnish', 'Korean',
] as const;

/** Shown first in the picker; the others are behind "More languages". */
export const MAIN_LANGUAGES = ['Arabic', 'French', 'English', 'Spanish', 'German', 'Italian', 'Portuguese', 'Turkish', 'Dutch'];

export interface CatalogChoice {
  languages: string[];
  /** Also movies and series in categories with no language ("NETFLIX MOVIES"); live ones are always kept. */
  international: boolean;
}

const NORDIC = ['Swedish', 'Danish', 'Norwegian', 'Finnish'];

/**
 * Whether a category goes into the viewer's list. Categories without a
 * language: live ones always (few, and they include international sports
 * channels), movies and series only with `international`.
 */
export function keepCategory(language: string | null, kind: 'live' | 'movie' | 'series', choice: CatalogChoice): boolean {
  if (language === null || language === 'Multi-language') return kind === 'live' || choice.international;
  if (language === 'Nordic languages') return choice.languages.some((chosen) => NORDIC.includes(chosen));
  return choice.languages.includes(language);
}

/** A valid choice from untrusted input, or null. */
export function parseCatalogChoice(value: unknown): CatalogChoice | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as { languages?: unknown; international?: unknown };
  if (!Array.isArray(input.languages)) return null;
  const known: readonly string[] = CATALOG_LANGUAGES;
  const languages = [...new Set(input.languages.filter((language): language is string => typeof language === 'string' && known.includes(language)))];
  if (languages.length === 0) return null;
  return { languages, international: input.international === true };
}
